import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy} from '@angular/core';
import {MatCard} from '@angular/material/card';
import {isUndefined} from '@aviary/store/common/typing.helpers';
import {NgClass} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {OrganizationSelectors} from '@store/organization/organization.selectors';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {PatronStatusComponent} from '@lib/components/patron-status/patron-status.component';
import {BarcodeComponent} from '@aviary/components/barcode/barcode.component';
import {OutlinedButtonComponent} from '@aviary/buttons/outlined-button/outlined-button.component';

const FLIP_DURATION_MILLISECONDS: number = 1500;

@Component({
  selector: 'px-library-card',
  host: {
    class: 'w-full min-h-[13.1875rem] max-w-[21.75rem] min-w-[16rem] [perspective:1000px]'
  },
  templateUrl: './library-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCard, NgClass, MatIcon, ActionButtonsComponent, SelectPipe, FormatPipe, PatronStatusComponent, BarcodeComponent, OutlinedButtonComponent]
})
export class LibraryCardComponent implements OnDestroy {

  public frontSelected: boolean = true; // Does the user *want* to see the front?
  public frontVisible: boolean = true;  // Is the front actually visible (after a timeout to enable the animation)?
  public readonly transitionDuration: string = `${FLIP_DURATION_MILLISECONDS}ms`;
  public readonly organizationSelectors = OrganizationSelectors.Auth;
  public readonly patronSelectors = PatronSelectors.Auth;

  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private flipTimeoutId?: ReturnType<typeof setTimeout>;

  public ngOnDestroy(): void {
    this.clearPendingFlipTimeout();
  }

  public onClickShowBackButton(): void {
    this.frontSelected = false;
    this.startFlip();
  }

  public onClickShowFrontButton(): void {
    this.frontSelected = true;
    this.startFlip();
  }

  private startFlip(): void {
    this.clearPendingFlipTimeout();

    if (this.prefersReducedMotion) {
      this.updateVisibleFace();
      return;
    }

    // We need to mark the front/back as visible at some point less than halfway through the flip to make the animation look right.
    this.flipTimeoutId = setTimeout(
      this.updateVisibleFace.bind(this),
      FLIP_DURATION_MILLISECONDS * 0.35 // Arbitrary fraction chosen because it looks good.
    );
  }

  private updateVisibleFace(): void {
    this.frontVisible = this.frontSelected;
    this.changeDetector.markForCheck();
  }

  private get prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private clearPendingFlipTimeout(): void {
    if (isUndefined(this.flipTimeoutId)) return;

    clearTimeout(this.flipTimeoutId);
    this.flipTimeoutId = undefined;
  }
}
